import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import Window from '../../../components/Window';
import InputWithoutTitle from '../../../components/InputWithoutTitle';
import getValidationErrors from '../../../utils/getValidationErrors';
import useQuery from '../../../hooks/query';
import { useCurrentCallback } from '../../../lib/use-current-effect';
import {
  sendWhatsappLinkEmail,
  startWhatsappLinkSession,
} from '../../../services/patient/PatientWhatsappLink';
import WhatsappLinkStep from '../Welcome/WhatsappLink';

import { Container, Field, SendButton, Outer } from './styles';

interface EmailFormProps {
  email: string;
}

type Estado =
  | 'email'
  | 'enviado'
  | 'trocando'
  | 'vinculo'
  | 'expirado'
  | 'fim'
  | 'desistiu';

/**
 * Retentativa do vínculo de WhatsApp (tarefa 0.35), para quem **pulou** o passo na tela de
 * boas-vindas.
 *
 * ⚠️ **Sem senha e sem código, de propósito.** O que autoriza é a posse da caixa de e-mail —
 * que já converte em tomada de conta hoje, pelo "esqueci minha senha" —, e tirar a senha do
 * caminho é o ponto: quem a esqueceu precisaria de dois e-mails antes de conseguir vincular.
 *
 * ⚠️ **A tela nunca diz se a conta existe.** O back responde igual nos dois casos e a cópia
 * daqui acompanha ("se existir uma conta ativada com esse e-mail..."). Dizer "e-mail não
 * encontrado" faria da página um oráculo de cadastro, e cadastro aqui é dado de saúde.
 *
 * Depois da troca do link, reusa o **mesmo** componente de vínculo da tela de boas-vindas —
 * são o mesmo par de rotas (`/auth/whatsapp/link-request` e `link-confirm`), com o token
 * restrito no lugar da sessão do app.
 */
const PatientWhatsappLink: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const query = useQuery();
  const token = useMemo(() => query.get('token'), [query]);

  const [estado, setEstado] = useState<Estado>(token ? 'trocando' : 'email');
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [nome, setNome] = useState('');

  /**
   * Com token na URL a troca acontece na montagem: o link é de **uso único**, e pedir um
   * clique antes de consumi-lo só cria a chance de a pessoa recarregar a página e queimá-lo
   * sem querer.
   */
  useEffect(() => {
    if (!token) return undefined;

    let atual = true;

    startWhatsappLinkSession(token)
      .then((sessao) => {
        if (!atual) return;
        setSessionToken(sessao.token);
        setNome(sessao.name);
        setEstado('vinculo');
      })
      .catch(() => {
        if (!atual) return;
        setEstado('expirado');
      });

    return () => {
      atual = false;
    };
  }, [token]);

  const handleSubmitEmail = useCurrentCallback(
    (isCurrent) => async (data: EmailFormProps) => {
      formRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          email: Yup.string()
            .email('Digite um e-mail válido')
            .required('O e-mail é obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
        }

        return;
      }

      setLoading(true);

      try {
        await sendWhatsappLinkEmail(data.email);

        if (isCurrent()) {
          setLoading(false);
          setEstado('enviado');
        }
      } catch (err) {
        // Só falha de rede ou de validação de formato chega aqui: conta inexistente
        // responde 204 como qualquer outra.
        if (err.response) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Não foi possível enviar o link agora.');
        }

        setLoading(false);
      }
    },
    [],
  );

  /**
   * O componente reusado tem "Vincular depois" nos dois estados dele, e aqui isso continua
   * valendo — mas o desfecho é outro: quem desiste **não** pode ler "está vinculado". O link
   * já foi queimado na troca, então a saída honesta é dizer como voltar.
   */
  const handleFinish = useCallback((dados: { whatsappLinked: boolean }) => {
    setEstado(dados.whatsappLinked ? 'fim' : 'desistiu');
  }, []);

  const pedirOutroLink = useCallback(() => {
    setSessionToken(null);
    setEstado('email');
  }, []);

  return (
    <Window>
      <Outer>
        {estado === 'email' && (
          <Container>
            <h1>Acompanhamento pelo WhatsApp</h1>
            <h2>
              Informe o e-mail do seu cadastro e enviamos um link para vincular
              o seu WhatsApp.
            </h2>
            <Form ref={formRef} onSubmit={handleSubmitEmail}>
              <Field>
                <label>E-mail:</label>
                <InputWithoutTitle name="email" type="email" />
              </Field>
              <SendButton loading={loading} type="submit">
                Enviar link
              </SendButton>
            </Form>
          </Container>
        )}

        {estado === 'enviado' && (
          <Container>
            <h1>Confira o seu e-mail</h1>
            <h2>
              Se existir uma conta ativada com esse e-mail, o link chega em
              instantes. Ele vale por 1 hora.
            </h2>
            <p>
              Não chegou? Confira a caixa de spam ou peça outro link com o
              e-mail que a equipe de saúde cadastrou.
            </p>
            <SendButton type="button" onClick={pedirOutroLink}>
              Usar outro e-mail
            </SendButton>
          </Container>
        )}

        {estado === 'trocando' && (
          <Container>
            <h1>Um instante</h1>
            <h2>Estamos abrindo o seu link.</h2>
          </Container>
        )}

        {estado === 'expirado' && (
          <Container>
            <h1>Link inválido ou expirado</h1>
            <h2>
              Cada link vale 1 hora e serve uma única vez. Peça outro informando
              o e-mail do seu cadastro.
            </h2>
            <SendButton type="button" onClick={pedirOutroLink}>
              Pedir outro link
            </SendButton>
          </Container>
        )}

        {estado === 'vinculo' && sessionToken && (
          <WhatsappLinkStep token={sessionToken} onFinish={handleFinish} />
        )}

        {estado === 'desistiu' && (
          <Container>
            <h1>Sem problema</h1>
            <h2>
              Quando quiser receber o acompanhamento pelo WhatsApp, volte a esta
              página e peça um link novo.
            </h2>
            <SendButton type="button" onClick={pedirOutroLink}>
              Pedir outro link
            </SendButton>
          </Container>
        )}

        {estado === 'fim' && (
          <Container>
            <h1>Tudo certo!</h1>
            <h2>
              {nome
                ? `${nome}, o seu WhatsApp está vinculado: é por lá que a Cinco vai falar com você.`
                : 'O seu WhatsApp está vinculado: é por lá que a Cinco vai falar com você.'}
            </h2>
          </Container>
        )}
      </Outer>
    </Window>
  );
};

export default PatientWhatsappLink;
